import { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export default function SignUp() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { signup, signInWithGoogle, checkEmailExists } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (password.length < 6) {
        setError(t('auth.passwordLength'));
        setLoading(false);
        return;
      }

      if (password !== confirmPassword) {
        setError(t('auth.passwordMismatch'));
        setLoading(false);
        return;
      }

      // Check if email already exists before attempting signup
      const emailExists = await checkEmailExists(email);
      if (emailExists) {
        setError(t('auth.emailAlreadyInUse'));
        setLoading(false);
        return;
      }

      await signup(email, password);
      navigate('/');
    } catch (error) {
      console.error('Signup error:', error);
      switch (error.code) {
        case 'auth/email-already-in-use':
          setError(t('auth.emailAlreadyInUse'));
          break;
        case 'auth/invalid-email':
          setError(t('auth.invalidEmail'));
          break;
        case 'auth/operation-not-allowed':
          setError(t('auth.operationNotAllowed'));
          break;
        case 'auth/weak-password':
          setError(t('auth.weakPassword'));
          break;
        default:
          setError(t('auth.signupFailed') + ': ' + error.message);
      }
      setLoading(false);
    }
  };

  const handleGoogleSignUp = async () => {
    setError('');
    setLoading(true);
    try {
      await signInWithGoogle();
      navigate('/');
    } catch (error) {
      console.error('Google signup error:', error);
      if (error.code === 'auth/account-exists-with-different-credential') {
        setError(t('auth.emailAlreadyInUse'));
      } else {
        setError(t('auth.googleSignupFailed') + ': ' + error.message);
      }
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 flex items-center justify-center">
      <div className="max-w-md w-full bg-gray-50 dark:bg-gray-800 rounded-lg shadow-lg p-8">
        <h2 className="text-3xl font-bold mb-6 text-gray-900 dark:text-gray-100">{t('auth.signup')}</h2>
        
        {error && (
          <div className="bg-red-100 dark:bg-red-500/20 border border-red-200 dark:border-red-500/50 text-red-600 dark:text-red-300 px-4 py-3 rounded-lg mb-6 animate-pulse">
            {error}
          </div>
        )}
        
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('auth.email')}
            </label>
            <input
              id="email"
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full p-3 bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-purple-400 focus:outline-none transition-all placeholder-gray-400"
              required
            />
          </div>
          
          <div className="space-y-2">
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('auth.password')}
            </label>
            <input
              id="password"
              type="password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full p-3 bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-purple-400 focus:outline-none transition-all placeholder-gray-400"
              required
              minLength={6}
            />
          </div>
          
          <div className="space-y-2">
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('auth.confirmPassword')}
            </label>
            <input
              id="confirmPassword"
              type="password"
              placeholder="••••••••"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full p-3 bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-purple-400 focus:outline-none transition-all placeholder-gray-400"
              required
              minLength={6}
            />
          </div>
          
          <button 
            type="submit" 
            className="w-full bg-purple-600 hover:bg-purple-500 text-white p-3 rounded-lg transition-colors transform hover:scale-[1.02] duration-200"
            disabled={loading}
          >
            {loading ? t('auth.signingUp') || 'Signing up...' : t('auth.signup')}
          </button>

          <div className="relative my-4">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300 dark:border-gray-600"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-gray-50 dark:bg-gray-800 text-gray-500 dark:text-gray-400">{t('auth.orContinueWith') || 'Or continue with'}</span>
            </div>
          </div>
          
          <button 
            type="button"
            onClick={handleGoogleSignUp}
            className="w-full flex items-center justify-center bg-white hover:bg-gray-100 text-gray-800 p-3 rounded-lg transition-colors transform hover:scale-[1.02] duration-200"
            disabled={loading}
          >
            <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
              <path
                fill="#4285F4"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="#34A853"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="#FBBC05"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="#EA4335"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
              <path fill="none" d="M1 1h22v22H1z" />
            </svg>
            Google
          </button>
          
          <div className="text-center text-gray-500 dark:text-gray-400 mt-4">
            <p>
              {t('auth.alreadyHaveAccount')} <Link to="/login" className="text-purple-600 dark:text-purple-400 hover:text-purple-500 dark:hover:text-purple-300 transition-colors">{t('auth.login')}</Link>
            </p>
          </div>
        </form>
      </div>
    </div>
  );
}